import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { authenticationService } from "../../../services/auth.service";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import toastOptions from "../../../utils/toastOptions";
import {
  faPencil,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IPersonDTO from "../../../models/IPersonDTO";
import DialogConfirm from "../../../components/DialogConfirm";
import Param from "../../../models/Param";
import { TablePage } from "../../../containers";
import CongregationSelect from "../../../components/congregation-select";

interface PersonListDTO {
  id: number;
  name: string;
  congregation: any;
  age: number;
}

export default function YouthPersonList() {
  const [isReadOnly, setReadOnly] = useState<boolean>();
  const [termSearch, setTermSearch] = useState<string>("");
  const [refresh, setRefresh] = useState<boolean>();
  const [isDialogDelete, setIsDialogDelete] = useState<boolean>(false);
  const [dialogDeleteText, setDialogDeleteText] = useState<string[]>([]);
  var personSelected = useRef(0);
  const [delayedSearchTerm, setDelayedSearchTerm] = useState("");
  const [congregations, setCongregations] = useState<Param[]>([]);
  const [congSearch, setCongSearch] = useState<number>(-1);
  const [total, setTotal] = useState<number>(0);
  const [pageNo, setPageNo] = useState<number>(1);
  const navigate = useNavigate();

  const columns = [
    {
      name: "Nome",
      sortable: true,
      grow: 2,
      cell: (row: PersonListDTO) => <div>
        <span>{row.name} - {row.age} anos</span><br/>
        <span className="uk-text-italic">({row.congregation.description})</span>
      </div>
    },
    {
      name: "Ações",
      button: true,
      sortable: false,
      cell: (row: IPersonDTO, index: number) => (
        <div className="uk-flex">
          <Link
            to={`/jovens/${row.id}`}
            uk-tooltip={`title: ${isReadOnly ? 'Visualizar' : 'Editar'}`}
            className="uk-margin-small-right"
          >
            <FontAwesomeIcon icon={isReadOnly ? faSearch : faPencil} />
          </Link>
          {!isReadOnly && (
            <a
              href="#"
              uk-tooltip="title: Remover"
              className="uk-margin-small-right"
              onClick={(ev) => {
                ev.preventDefault();
                handleRemove(row);
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </a>
          )}
        </div>
      ),
    }
  ];

  useEffect(() => {
    setReadOnly(!authenticationService.canChangeYouth());
    init();
  }, []);

  const init = async () => {
    const { data: dataCongregations } = await api.get("congregations/dashboard");

    setCongregations(dataCongregations);
  }

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm]);

  const fetchPeople = async (page: number, limit: number) => {
    try {
      let requestParams = {
        page: page,
        limit: limit,
        congregation: congSearch
      } as any;

      if (termSearch) {
        requestParams = { ...requestParams, name: termSearch?.toUpperCase() };
      }

      const response = await api.get(`youths`, {
        params: requestParams
      });

      setTotal(response.data.total);

      setPageNo(page);

      return response;
    } catch {
      return { data: [], total: 0 };
    }
  };

  const handleRemove = async (row: IPersonDTO) => {
    setDialogDeleteText([
      `Deseja realmente remover ${row.name}?`
    ]);
    personSelected.current = row.id;
    setIsDialogDelete(true);
  };

  const dialogClose = () => {
    setIsDialogDelete(false);
  };

  const handleSearchByName = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
    }
  };

  const handleCongregation = async(congregation: number) => {
    setCongSearch(congregation);
    setRefresh(!refresh);
  }

  const dialogConfirm = async () => {
    try {
      await api.delete(`/youths/${personSelected.current}`);
      setIsDialogDelete(false);
      toast.success("Jovem removido com sucesso!", toastOptions);
      setRefresh(!refresh);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  const handleOptions = () => {
    return (
      <div className="uk-grid-small" uk-grid="true">
        <div className="uk-search uk-search-default uk-width-1-2@m">
          <span
            className="uk-search-icon-flip"
            uk-search-icon="true"
          ></span>
          <input
            className="uk-search-input uk-input uk-form-small"
            type="search"
            placeholder="Pesquise por nome..."
            style={{ textTransform: "uppercase" }}
            onChange={(ev) => {
              handleSearchByName(ev.target.value);
            }}
          />
        </div>
        <div className="uk-flex uk-flex-middle uk-width-1-3@m">
          <CongregationSelect afterSelect={(id: number)=> handleCongregation(id)}/>
        </div>
      </div>
    )
  };

  return (
    <div id="page-event-person">
      <TablePage 
        title="Jovens Ativos"
        columns={columns}
        fetch={fetchPeople}
        refresh={refresh}
        showSubHeader
        totalRecords={total}
        labelTotalRecords={`${total} ${total === 1 ? "Jovem" : "Jovens"}`}
        options={handleOptions()}
        backUrl={`/umadeub`}
        messageEmpty={<span>Nenhum <b>jovem</b> encontrado!</span>}
        pageNo={pageNo}
        showAddButton
        handleAdd={() => navigate('/jovens/novo')}
      />      
      <DialogConfirm
        open={isDialogDelete}
        text={dialogDeleteText}
        afterClose={dialogClose}
        handleConfirmation={(confirmationCode) =>
          dialogConfirm()
        }
      />
    </div>
  );
}
